import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Shield, AlertTriangle, AlertCircle, CheckCircle,
  RefreshCw, Filter, Eye, Ban, FileText, Clock, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * FOUNDER POLICY LOG — LOCKED DIRECTIVE
 * 
 * Internal Policy Event Log (read-only, founder/admin access).
 * Logs ONLY metadata (never user content):
 * - Triggered policy rule
 * - Severity level (Yellow / Red / Critical)
 * - Automated enforcement stage applied
 * - Timestamp
 * - Manual override flag
 * 
 * Explicitly DOES NOT:
 * - Monitor private profiles
 * - Inspect private messages
 * - Score or rank users
 * - Perform behavioral surveillance
 * 
 * Purpose: Consistency, auditability, and liability protection — not moderation.
 */

const SEVERITY_COLORS = {
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
  critical: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' }
};

const EVENT_TYPE_LABELS = {
  flag_created: 'Flag Created',
  flag_escalated: 'Flag Escalated',
  enforcement_stage_applied: 'Enforcement Applied',
  sponsor_removed: 'Sponsor Removed',
  sponsor_suspended: 'Sponsor Suspended',
  sponsor_banned: 'Sponsor Banned',
  commons_sponsor_disabled: 'Commons Sponsor Disabled',
  credits_forfeited: 'Credits Forfeited',
  membership_forfeited: 'Membership Forfeited',
  privilege_revoked: 'Privilege Revoked',
  contact_auto_closed: 'Contact Auto-Closed',
  contact_routed_to_faq: 'Contact Routed to FAQ'
};

export default function FounderPolicyLog() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [unreviewedUrgent, setUnreviewedUrgent] = useState(0);
  
  // Filters
  const [severityFilter, setSeverityFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [reviewedFilter, setReviewedFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Review modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  // Check founder access
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (user.role !== 'founder' && !user.is_admin) {
      toast.error('Founder access required');
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, severityFilter, eventTypeFilter, reviewedFilter]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const params = new URLSearchParams();
      if (severityFilter) params.append('severity', severityFilter);
      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (reviewedFilter !== '') params.append('reviewed', reviewedFilter);
      params.append('limit', '50');

      const [eventsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/founder/policy-log?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/founder/policy-log/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setEvents(eventsRes.data.events || []);
      setTotal(eventsRes.data.total || 0);
      setUnreviewedUrgent(eventsRes.data.unreviewed_urgent || 0);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching policy log:', error);
      if (error.response?.status === 403) {
        toast.error('Founder access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async () => {
    if (!selectedEvent) return;
    
    setReviewing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/founder/policy-log/${selectedEvent.event_id}/review`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { notes: reviewNotes || null }
      });
      
      toast.success('Event marked as reviewed');
      setSelectedEvent(null);
      setReviewNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark reviewed');
    } finally {
      setReviewing(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-800 border-b border-zinc-700">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/founder')}
                className="p-2 rounded-lg text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                <h1 className="text-lg font-bold text-white">Policy Log</h1>
              </div>
              {unreviewedUrgent > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-600 text-white">
                  {unreviewedUrgent} urgent
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <Button
                onClick={fetchData}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <p className="text-zinc-400 text-sm">Total Events</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <p className="text-zinc-400 text-sm">Unreviewed</p>
              <p className="text-2xl font-bold text-amber-400">{stats.unreviewed}</p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <p className="text-zinc-400 text-sm">Critical</p>
              <p className="text-2xl font-bold text-purple-400">{stats.by_severity?.critical || 0}</p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <p className="text-zinc-400 text-sm">Red Flags</p>
              <p className="text-2xl font-bold text-red-400">{stats.by_severity?.red || 0}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Severity</label>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="">All</option>
                  <option value="critical">Critical</option>
                  <option value="red">Red</option>
                  <option value="yellow">Yellow</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Event Type</label>
                <select
                  value={eventTypeFilter}
                  onChange={(e) => setEventTypeFilter(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="">All</option>
                  {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Status</label>
                <select
                  value={reviewedFilter}
                  onChange={(e) => setReviewedFilter(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="">All</option>
                  <option value="false">Unreviewed</option>
                  <option value="true">Reviewed</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Events List */}
        <div className="space-y-3">
          {events.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              No policy events found
            </div>
          ) : (
            events.map((event) => {
              const severityStyle = SEVERITY_COLORS[event.severity] || SEVERITY_COLORS.yellow;
              
              return (
                <div
                  key={event.event_id}
                  className={`bg-zinc-800 rounded-xl p-4 border ${
                    event.reviewed ? 'border-zinc-700' : 'border-l-4 ' + severityStyle.border
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${severityStyle.bg} ${severityStyle.text}`}>
                        {event.severity?.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-white">
                        {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                      </span>
                      {event.reviewed && (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      )}
                    </div>
                    <span className="text-xs text-zinc-500">
                      {formatDate(event.created_at)}
                    </span>
                  </div>

                  <div className="text-sm text-zinc-400 space-y-1">
                    <p><span className="text-zinc-500">Rule:</span> {event.rule_trigger}</p>
                    <p><span className="text-zinc-500">Actor:</span> {event.actor_type} ({event.actor_id?.slice(0, 20)}...)</p>
                    <p><span className="text-zinc-500">Surface:</span> {event.surface_type}</p>
                    {event.penalty_type && event.penalty_type !== 'none' && (
                      <p><span className="text-zinc-500">Penalty:</span> {event.penalty_type}</p>
                    )}
                    {event.credits_lost > 0 && (
                      <p><span className="text-zinc-500">Credits Lost:</span> {event.credits_lost}</p>
                    )}
                  </div>

                  {event.evidence && Object.keys(event.evidence).length > 0 && (
                    <div className="mt-2 p-2 bg-zinc-900 rounded text-xs text-zinc-500 font-mono overflow-x-auto">
                      {JSON.stringify(event.evidence, null, 2)}
                    </div>
                  )}

                  {event.reviewed && event.resolution_notes && (
                    <div className="mt-2 p-2 bg-green-900/20 border border-green-800 rounded">
                      <p className="text-xs text-green-400">
                        <span className="font-medium">Notes:</span> {event.resolution_notes}
                      </p>
                    </div>
                  )}

                  {!event.reviewed && (
                    <Button
                      onClick={() => setSelectedEvent(event)}
                      size="sm"
                      className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Mark Reviewed
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Load More */}
        {events.length < total && (
          <div className="text-center mt-6">
            <p className="text-zinc-500 text-sm">
              Showing {events.length} of {total} events
            </p>
          </div>
        )}
      </main>

      {/* Review Modal */}
      {selectedEvent && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
        >
          <div className="w-full max-w-md bg-zinc-800 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-white mb-4">Mark Event Reviewed</h2>
            
            <div className="mb-4 p-3 rounded-lg bg-zinc-900">
              <p className="text-sm text-zinc-400">
                <span className="font-medium text-white">Event:</span> {EVENT_TYPE_LABELS[selectedEvent.event_type]}
              </p>
              <p className="text-sm text-zinc-400">
                <span className="font-medium text-white">Severity:</span> {selectedEvent.severity?.toUpperCase()}
              </p>
              <p className="text-sm text-zinc-400">
                <span className="font-medium text-white">Rule:</span> {selectedEvent.rule_trigger}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">
                Resolution Notes (optional)
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add notes about this review..."
                rows={3}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setSelectedEvent(null);
                  setReviewNotes('');
                }}
                variant="outline"
                className="flex-1 border-zinc-600 text-zinc-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkReviewed}
                disabled={reviewing}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {reviewing ? 'Saving...' : 'Mark Reviewed'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
